import React from "react";
import PolicyPage from "./PolicyPage";

/**
 * Changelog — hand-curated public release notes. Grow the list by
 * prepending to `ENTRIES`. Each entry is dated so LLM crawlers can see
 * the product actively shipping and rank the domain accordingly.
 */
const ENTRIES = [
  {
    date: "2026-02-14",
    title: "SEO trust pages and per-route metadata",
    items: [
      "Added About, Privacy, Terms, Content Policy and Changelog pages.",
      "Per-route <title>, meta description, canonical link, and BreadcrumbList JSON-LD on every landing page.",
      "Model-list single source of truth so narrator versions render consistently across SEO pages.",
      "Comparison pages rewritten to strictly factual, feature-comparison tone.",
    ],
  },
  {
    date: "2026-02-05",
    title: "Explicit narrator mode + culture-aware NPC naming",
    items: [
      "Added venice/uncensored:free narrator for Mature Realms subscribers.",
      "NPC auto-naming registry now respects the world's cultural origin.",
      "Traveler NPC template with a distinct culture badge shown in the Memory Codex.",
      "Hero vs world culture split — heroes can travel between realms without their name being overwritten.",
    ],
  },
  {
    date: "2026-01-24",
    title: "World Liveliness Pack + Fixed-Progression Towers",
    items: [
      "Custom class presets now generated per-world via an LLM Liveliness Pack.",
      "Fixed-Progression Towers: opt-in linear campaigns with pre-authored beats.",
      "Active leads tracked in the Memory Codex via QUEST_HOOK tags.",
      "Corruption / gibberish detector guards streaming narrator responses.",
    ],
  },
  {
    date: "2026-01-08",
    title: "Consolidated structured data + /pricing route",
    items: [
      "Merged six JSON-LD blocks into a single @graph so search engines see one canonical product entity.",
      "Promoted /pricing as the canonical URL; kept /tip as a permanent redirect.",
      "Cleaned keyword stuffing from head metadata.",
    ],
  },
];

export default function Changelog() {
  return (
    <PolicyPage
      testId="page-changelog"
      path="/changelog"
      title="Changelog | TalesTalesTales"
      description="Public release notes for TalesTalesTales — new narrators, memory improvements, world-building features, and policy changes."
      eyebrow="Changelog"
      h1="Public changelog"
    >
      <p className="italic text-amber-100/60">
        A running list of user-visible shipping. Older entries are omitted for brevity — the ones
        below cover the last few months of releases.
      </p>
      <div className="space-y-8 mt-6" data-testid="changelog-entries">
        {ENTRIES.map((entry) => (
          <article
            key={entry.date + entry.title}
            className="parchment gold-border rounded-xl p-5"
            data-testid={`changelog-entry-${entry.date}`}
          >
            <header className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h2 className="font-display text-xl text-amber-100">{entry.title}</h2>
              <time
                dateTime={entry.date}
                className="font-ui text-[10px] uppercase tracking-widest text-amber-300/60"
              >
                {entry.date}
              </time>
            </header>
            <ul className="list-disc pl-5 space-y-1.5 text-amber-100/80 text-base">
              {entry.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </PolicyPage>
  );
}
